import {
    imageDisplaySizes,
    type SizeName,
} from 'src/constants/image-display-sizes'
import type {
    AspectRatio,
    ImageSizeMapInterface,
    ResolveImageUrlArgs,
    ImageDimensions,
} from '../shared'

const STANDARD_SIZE_NAMES_FOR_IMAGE_RESIZER: SizeName[] = [
    'p1',
    'p4',
    'd1',
    'd3',
    'd6',
    'd8',
    'd12',
]

export function resolveCloudflareImageUrl({
    imageResizerBaseUrl,
    imageFilePath,
    dimensions,
    crop,
}: ResolveImageUrlArgs & {
    imageResizerBaseUrl: string // change imageResizerBaseUrl argument from optional to required
}) {
    let options = `width=${dimensions.width},height=${dimensions.height}`
    if (crop) {
        options += `,fit=crop`
    }
    // based on URL format options documented at https://developers.cloudflare.com/images/transform-images/transform-via-url/
    return `${imageResizerBaseUrl}${options}/${imageFilePath}`
}

/**
 * Loops through our source size presets and selects the closest source size that's at
 * least as large as the requested display size
 */
export function selectClosestSourceSize({
    displaySizeName,
    aspectRatio,
    sortedSourceSizes,
}: {
    displaySizeName: SizeName
    aspectRatio: AspectRatio
    sortedSourceSizes: ImageSizeMapInterface
}): ImageDimensions | null {
    const desiredDisplayWidth = imageDisplaySizes.getDimensions(
        aspectRatio,
        displaySizeName
    )!.width

    // note: this assumes that all source sizes are 2x sizes for high-res screens
    const closestSourceSize = [...sortedSourceSizes.values()].find(
        (size) => size[aspectRatio].width / 2 >= desiredDisplayWidth
    )

    return closestSourceSize ? closestSourceSize[aspectRatio] : null
}

/**
 * Returns the sizes specified above in STANDARD_SIZE_NAMES_FOR_IMAGE_RESIZER,
 * multiplying each of the display size dimensions by 2 for crisper images on high-res screens
 */
export function defaultHighResSourceSizes(): ImageSizeMapInterface {
    const doubleSizes = (dimensions: ImageDimensions) => ({
        width: dimensions.width * 2,
        height: dimensions.height * 2,
    })

    const sourceSizes = new Map(
        STANDARD_SIZE_NAMES_FOR_IMAGE_RESIZER.map((sizeName) => [
            sizeName,
            Object.fromEntries(
                Object.entries(imageDisplaySizes.get(sizeName)!).map(
                    ([aspectRatio, dimensions]) => [
                        aspectRatio,
                        doubleSizes(dimensions as ImageDimensions),
                    ]
                )
            ),
        ])
    ) as ImageSizeMapInterface

    return new Map(sortImageSizes(sourceSizes))
}

function sortImageSizes(imageSizes: ImageSizeMapInterface) {
    return [...imageSizes].sort((a, b) => {
        const [, sizesA] = a
        const [, sizesB] = b
        return sizesA['1/1'].width - sizesB['1/1'].width
    })
}
